import React, { useState } from 'react';
import { MapPin, Phone, Globe, Clock, Star, ExternalLink, Search } from 'lucide-react';
import SEO from './SEO';

interface FitterLocation {
  id: string;
  name: string;
  type: 'independent' | 'retail' | 'manufacturer';
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website?: string;
  rating: number;
  reviews: number;
  services: string[];
  brands: string[];
  priceRange: '$' | '$$' | '$$$';
  hours: string;
  featured: boolean;
}

// Sample fitter data - in production this would come from a database
const SAMPLE_FITTERS: FitterLocation[] = [
  {
    id: '1',
    name: 'Club Champion',
    type: 'independent',
    address: '123 Golf Lane',
    city: 'San Diego',
    state: 'CA',
    zip: '92101',
    phone: '(858) 555-0100',
    website: 'https://clubchampiongolf.com',
    rating: 4.9,
    reviews: 245,
    services: ['Full Bag Fitting', 'Driver Fitting', 'Iron Fitting', 'Putter Fitting', 'Shaft Analysis'],
    brands: ['Titleist', 'TaylorMade', 'Callaway', 'Ping', 'Cobra', 'Mizuno'],
    priceRange: '$$$',
    hours: 'Mon-Sat 9AM-7PM',
    featured: true
  },
  {
    id: '2',
    name: 'Golf Galaxy',
    type: 'retail',
    address: '456 Fairway Dr',
    city: 'Los Angeles',
    state: 'CA',
    zip: '90001',
    phone: '(310) 555-0200',
    website: 'https://golfgalaxy.com',
    rating: 4.3,
    reviews: 189,
    services: ['Full Bag Fitting', 'Driver Fitting', 'Iron Fitting', 'TrackMan Analysis'],
    brands: ['TaylorMade', 'Callaway', 'Titleist', 'Ping', 'Cleveland'],
    priceRange: '$$',
    hours: 'Mon-Sun 10AM-9PM',
    featured: false
  },
  {
    id: '3',
    name: 'TaylorMade Performance Lab',
    type: 'manufacturer',
    address: '789 Driver Way',
    city: 'Carlsbad',
    state: 'CA',
    zip: '92008',
    phone: '(760) 555-0300',
    website: 'https://taylormade.com',
    rating: 4.8,
    reviews: 312,
    services: ['Full Bag Fitting', 'Driver Fitting', 'Custom Build', 'Tour-Level Analysis'],
    brands: ['TaylorMade'],
    priceRange: '$$$',
    hours: 'By appointment',
    featured: true
  },
  {
    id: '4',
    name: "Dick's Sporting Goods",
    type: 'retail',
    address: '321 Sports Blvd',
    city: 'Phoenix',
    state: 'AZ',
    zip: '85001',
    phone: '(602) 555-0400',
    website: 'https://dickssportinggoods.com',
    rating: 4.0,
    reviews: 156,
    services: ['Basic Fitting', 'Driver Fitting', 'Iron Fitting'],
    brands: ['Callaway', 'TaylorMade', 'Cobra', 'Top Flite'],
    priceRange: '$',
    hours: 'Mon-Sat 9AM-9PM, Sun 10AM-7PM',
    featured: false
  },
  {
    id: '5',
    name: 'True Spec Golf',
    type: 'independent',
    address: '555 Precision Lane',
    city: 'Scottsdale',
    state: 'AZ',
    zip: '85251',
    phone: '(480) 555-0500',
    website: 'https://truespecgolf.com',
    rating: 4.9,
    reviews: 423,
    services: ['Full Bag Fitting', 'Driver Fitting', 'Iron Fitting', 'Wedge Fitting', 'Putter Fitting', '3D Motion Capture'],
    brands: ['All Major Brands'],
    priceRange: '$$$',
    hours: 'By appointment',
    featured: true
  }
];

const FindFitter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPrice, setFilterPrice] = useState<string>('all');

  const filteredFitters = SAMPLE_FITTERS.filter(fitter => {
    const matchesSearch = 
      fitter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fitter.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fitter.state.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === 'all' || fitter.type === filterType;
    const matchesPrice = filterPrice === 'all' || fitter.priceRange === filterPrice;
    
    return matchesSearch && matchesType && matchesPrice;
  });

  // Sort: featured first, then by rating
  const sortedFitters = [...filteredFitters].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return b.rating - a.rating;
  });

  return (
    <>
      <SEO 
        title="Find a Club Fitter Near You"
        description="Find professional golf club fitters in your area. Get expert club fitting for drivers, irons, wedges, and putters. Compare local fitters and book appointments."
        url="/find-fitter"
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <MapPin className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Find a Fitter</h1>
              <p className="text-amber-100">Sample listings for illustration only — not a live marketplace</p>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
          These are sample / demo fitter listings with placeholder addresses and ratings.
          They are not real inventory and should not be used to book an appointment.
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by city, state, or fitter name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div className="flex gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-amber-500 text-gray-700"
              >
                <option value="all">All Types</option>
                <option value="independent">Independent</option>
                <option value="retail">Retail</option>
                <option value="manufacturer">Manufacturer</option>
              </select>
              <select
                value={filterPrice}
                onChange={(e) => setFilterPrice(e.target.value)}
                className="px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-amber-500 text-gray-700"
              >
                <option value="all">Any Price</option>
                <option value="$">$ Budget</option>
                <option value="$$">$$ Mid-Range</option>
                <option value="$$$">$$$ Premium</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {sortedFitters.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No fitters found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            sortedFitters.map(fitter => (
              <div 
                key={fitter.id}
                className={`bg-white rounded-xl shadow-lg overflow-hidden ${
                  fitter.featured ? 'ring-2 ring-amber-400' : ''
                }`}
              >
                {fitter.featured && (
                  <div className="bg-amber-500 text-white text-xs font-semibold px-4 py-1">
                    Sample listing
                  </div>
                )}
                <div className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{fitter.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          fitter.type === 'independent' ? 'bg-purple-100 text-purple-700' :
                          fitter.type === 'manufacturer' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {fitter.type.charAt(0).toUpperCase() + fitter.type.slice(1)}
                        </span>
                        <span className="text-amber-600 font-semibold">{fitter.priceRange}</span>
                      </div>
                      
                      <div className="flex items-center gap-1 mb-3">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="font-semibold text-gray-900">{fitter.rating}</span>
                        <span className="text-gray-500 text-sm">({fitter.reviews} reviews)</span>
                      </div>

                      <div className="space-y-1 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{fitter.address}, {fitter.city}, {fitter.state} {fitter.zip}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{fitter.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span>{fitter.hours}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        {fitter.services.slice(0, 4).map((service, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {service}
                          </span>
                        ))}
                        {fitter.services.length > 4 && (
                          <span className="text-xs text-gray-500">+{fitter.services.length - 4} more</span>
                        )}
                      </div>
                    </div>

                    <div className="flex sm:flex-col gap-2">
                      {fitter.website && (
                        <a
                          href={fitter.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                        >
                          <Globe className="w-4 h-4" />
                          Visit Website
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <a
                        href={`tel:${fitter.phone.replace(/\D/g, '')}`}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        <Phone className="w-4 h-4" />
                        Call Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* CTA for fitters */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 text-white text-center">
          <h3 className="text-xl font-bold mb-2">Are you a club fitter?</h3>
          <p className="text-gray-300 mb-4">
            Partner with ClubIQ to reach more golfers looking for professional fitting services.
          </p>
          <a
            href="/contact?subject=fitter-partnership"
            className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Become a Partner
          </a>
        </div>
      </div>
    </>
  );
};

export default FindFitter;

